/**
 * Profile Selector Component (Phase 5 — Step 5.1).
 */

import React, { useState } from 'react';
import { getProfiles, createProfile, deleteProfile, getActiveProfileId, type Profile } from '@/auth/profileManager';
import { Modal, Button } from '../shared';

interface ProfileSelectorProps {
  isOpen: boolean;
  onClose: () => void;
}

export function ProfileSelector({ isOpen, onClose }: ProfileSelectorProps): React.ReactElement {
  const [profiles, setProfiles] = useState<Profile[]>(getProfiles);
  const [newName, setNewName] = useState('');
  const activeId = getActiveProfileId();

  const handleCreate = (): void => {
    if (!newName.trim()) return;
    createProfile(newName.trim());
    setProfiles(getProfiles());
    setNewName('');
  };

  const handleDelete = (id: string): void => {
    void deleteProfile(id);
    setProfiles(getProfiles());
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Profile Manager">
      <div className="profile-selector">
        <div className="profile-selector__list">
          {profiles.map((p) => (
            <div key={p.id} className={`profile-selector__item ${p.id === activeId ? 'profile-selector__item--active' : ''}`}>
              <span className="profile-selector__avatar" style={{ background: p.avatarColor }}>
                {p.name.charAt(0).toUpperCase()}
              </span>
              <div className="profile-selector__info">
                <span className="profile-selector__name">{p.name}</span>
                <span className="profile-selector__type">{p.type}</span>
              </div>
              {p.id !== activeId && (
                <button type="button" className="profile-selector__delete" onClick={() => handleDelete(p.id)} aria-label={`Delete ${p.name}`}>
                  &times;
                </button>
              )}
            </div>
          ))}
          {profiles.length === 0 && <p>No profiles yet. Create one below.</p>}
        </div>

        <div className="profile-selector__create">
          <input
            type="text"
            placeholder="New profile name"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
            aria-label="New profile name"
          />
          <Button variant="primary" size="sm" onClick={handleCreate} disabled={!newName.trim()}>Create</Button>
        </div>
      </div>
    </Modal>
  );
}
